<script setup>
import ClipboardJS from "clipboard";

const route = useRoute();
const router = useRouter();
const nuxtApp = useNuxtApp();
const localePath = useLocalePath();

const { t } = useI18n();
const { setShortMeta } = useMeta();

const props = defineProps({
    data: {
        type: Array,
        required: true,
    },
});

const sizeMap = { "9:16": "l", "16:9": "w", "1:1": "s" };
const id = computed(() => route.params.id);

const shortsData = computed(() =>
    props.data?.find((item) => item?.id == id.value),
);

// If no matching short found, throw 404
if (!shortsData.value) {
    throw createError({
        statusCode: 404,
        statusMessage: "Video Not Found",
        fatal: true,
    });
}

const shortMedia = computed(() =>
    nuxtApp?.payload?.data?.["fetchMedias-short-media"]?.find(
        (item) => item?.id == shortsData?.value?.featured_media,
    ),
);

const size = computed(() => sizeMap[shortsData?.value?.aspectRatio]);

const canUseClipboard = ref(false);
const canCopy = ref(false);
const isCopied = ref(false);
const shareURL = ref("");

if (import.meta.client) {
    canUseClipboard.value = ClipboardJS.isSupported();
    canCopy.value =
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function" &&
        navigator.permissions;
    shareURL.value = window.location.href;
}

const handleCopySuccess = () => {
    isCopied.value = true;
    setTimeout(() => {
        isCopied.value = false;
    }, 3000);
};

const handleCopyFail = (error) => {
    // 使用 navigator clipboard
    if (canCopy.value) alert(t("notification.useAnotherWay"));
    // 使用 clipboard.js
    else if (!ClipboardJS.isSupported())
        alert(t("notification.browserNotSupported"));
    else alert(t("notification.linkCopyFailed"));
};

const copyURL = () => {
    navigator.clipboard
        .writeText(shareURL.value)
        .then(() => handleCopySuccess())
        .catch(() => handleCopyFail());
};

const handleShareToFacebook = () => {
    const href = shareURL.value;
    window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${href}`,
        "_blank",
    );
};

const handleShareToLine = () => {
    const href = shareURL.value;
    window.open(
        `https://social-plugins.line.me/lineit/share?url=${href}`,
        "_blank",
    );
};
const handleShareToTwitter = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?url=${shareURL.value}`;
    window.open(twitterUrl, "_blank");
};

const close = () => {
    router.push(localePath("/gplay"));
};
const { isPressed, pressClose } = usePressClose(close);

const preventScroll = (e) => {
    e.preventDefault();
    e.stopPropagation();
};

const lockScroll = () => {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.addEventListener("wheel", preventScroll, { passive: false });
};

const unlockScroll = () => {
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
    document.body.removeEventListener("wheel", preventScroll);
};

onMounted(() => {
    lockScroll();

    const clipBoard_mobile = new ClipboardJS("#copy-button-m");
    const clipBoard_desktop = new ClipboardJS("#copy-button-d");
    clipBoard_mobile.on("success", handleCopySuccess);
    clipBoard_mobile.on("error", handleCopyFail);
    clipBoard_desktop.on("success", handleCopySuccess);
    clipBoard_desktop.on("error", handleCopyFail);
});

onUnmounted(() => {
    unlockScroll();
});

await setShortMeta(shortsData.value, shortMedia.value);
</script>

<template>
    <div
        class="fixed left-0 top-0 z-[400] h-full w-full overflow-y-auto bg-base-black-90"
    >
        <button
            @click="pressClose"
            class="_button-close flex items-center justify-center transition-transform hover:scale-105 focus:scale-95"
            :class="{ 'animate-buttonPress': isPressed }"
        >
            <SvgClose24 class="h-[6.1vw] w-[6.1vw] xs:h-[24px] xs:w-[24px]" />
        </button>

        <div
            class="pointer-events-none relative flex justify-center lg:min-h-[--window-height]"
        >
            <div
                class="pointer-events-auto absolute left-0 top-0 h-full w-full"
                @click="close"
            ></div>
            <div
                class="relative px-[4.07vw] pb-[4.07vw] pt-[calc(var(--window-height)*0.1517)] xs:px-[16px] xs:pb-[16px] sm:px-0 sm:pb-[20px] lg:flex lg:w-full lg:items-center lg:justify-center lg:pb-0 lg:pt-0"
                :class="{
                    'sm:w-[394px]': size === 'l',
                    'sm:w-[95.83%] sm:max-w-[736px] lg:max-w-[unset]':
                        size === 'w',
                    'sm:w-[520px]': size === 's',
                }"
            >
                <div
                    class="flex w-full flex-col gap-[12.21vw] pb-[10.18vw] xs:gap-[48px] xs:pb-[40px] lg:flex-row lg:items-center lg:justify-center lg:gap-[5.29vw] lg:pb-0 3xl:gap-[101.6px]"
                >
                    <div
                        class="pointer-events-auto w-full overflow-hidden rounded-[4.07vw] border border-base-white-30 xs:rounded-[16px]"
                        :class="{
                            'aspect-[9/16] lg:mr-[5.03vw] lg:w-[27.78vw] 3xl:mr-[96.5px] 3xl:w-[533.3px]':
                                size === 'l',
                            'aspect-[16/9] lg:w-[49.4vw] 3xl:w-[948.6px]':
                                size === 'w',
                            'aspect-[1/1] lg:w-[34.39vw] 3xl:w-[660.3px]':
                                size === 's',
                        }"
                    >
                        <iframe
                            class="h-full w-full"
                            :src="`https://www.youtube.com/embed/${shortsData?.url}`"
                            title="YouTube video player"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerpolicy="strict-origin-when-cross-origin"
                            allowfullscreen
                        ></iframe>
                    </div>
                    <div
                        class="flex flex-col gap-[8.14vw] xs:gap-[32px] lg:h-[88vh] lg:w-[20.37vw] lg:justify-center lg:gap-12 3xl:w-[391.1px]"
                    >
                        <div
                            class="slt-h6 pointer-events-auto flex items-center gap-[4px] text-base-bgGrey"
                        >
                            <div
                                class="flex h-[10.18vw] items-center justify-center rounded-full bg-base-deepGrey px-[5.09vw] xs:h-[40px] xs:px-[20px]"
                                v-for="category in shortsData?.categories"
                                :key="category"
                            >
                                <p>{{ category }}</p>
                            </div>
                        </div>
                        <div class="flex flex-col gap-[6.1vw] xs:gap-[24px]">
                            <h1
                                class="slt-h3-ttt pointer-events-auto text-base-white"
                            >
                                <span
                                    v-for="(text, index) in shortsData?.title"
                                    :key="text"
                                    ><br v-if="index > 0" />{{ text }}</span
                                >
                            </h1>
                            <div>
                                <p
                                    class="slt-body-1 slt-text-shadow pointer-events-auto text-base-white"
                                >
                                    <span
                                        v-for="(
                                            text, index
                                        ) in shortsData?.description"
                                        :key="text"
                                        ><br v-if="index > 0" />{{ text }}</span
                                    >
                                </p>
                            </div>
                            <h2
                                v-if="shortsData?.tags?.length > 0"
                                class="slt-body-3 pointer-events-auto text-base-grey"
                            >
                                {{ `#${shortsData?.tags.join(" #")}` }}
                            </h2>
                        </div>
                        <!-- SNS -->
                        <div
                            class="flex flex-col gap-[4.07vw] xs:gap-[16px] lg:hidden"
                        >
                            <div
                                class="pointer-events-auto flex gap-[2.03vw] pl-[2.03vw] xs:gap-[8px] xs:pl-[8px]"
                            >
                                <div
                                    class="slt-share-icon"
                                    @click="handleShareToLine"
                                    v-sendGAsignal="{
                                        event_category: `/gplay/${id}`,
                                        event_label: '分享至 line',
                                    }"
                                >
                                    <SvgLine24 class="icon" />
                                </div>
                                <div
                                    class="slt-share-icon"
                                    @click="handleShareToFacebook"
                                    v-sendGAsignal="{
                                        event_category: `/gplay/${id}`,
                                        event_label: '分享至 facebook',
                                    }"
                                >
                                    <SvgFb24 class="icon" />
                                </div>
                                <div
                                    class="slt-share-icon"
                                    @click="handleShareToTwitter"
                                    v-sendGAsignal="{
                                        event_category: `/gplay/${id}`,
                                        event_label: '分享至 twitter',
                                    }"
                                >
                                    <SvgTwitter24 class="icon" />
                                </div>
                                <div
                                    v-if="canCopy"
                                    id="copy-button-m"
                                    :data-clipboard-text="shareURL"
                                    class="slt-share-icon"
                                    v-sendGAsignal="{
                                        event_category: `/gplay/${id}`,
                                        event_label: '複製網址',
                                    }"
                                >
                                    <SvgLink24 class="icon" />
                                </div>
                                <div
                                    v-else-if="canUseClipboard"
                                    id="copy-button-m"
                                    @click="copyURL"
                                    class="slt-share-icon"
                                    v-sendGAsignal="{
                                        event_category: `/gplay/${id}`,
                                        event_label: '複製網址',
                                    }"
                                >
                                    <SvgLink24 class="icon" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <transition name="fade">
                    <div
                        v-show="isCopied"
                        class="fixed bottom-[5vh] left-[50%] translate-x-[-50%] text-center lg:bottom-[5vh]"
                    >
                        <p
                            class="slt-body-1 rounded-[5px] bg-[rgba(0,0,0,0.5)] px-[2.5vw] py-[1.5vw] text-white sm:px-[20px] sm:py-[10px]"
                        >
                            {{ $t("notification.linkCopied") }}
                        </p>
                    </div>
                </transition>
                <div
                    class="absolute bottom-[48px] right-[40px] hidden flex-col gap-[16px] lg:flex lg:items-center"
                >
                    <p
                        lang="en"
                        class="slt-vertical-text slt-body-1 bold pointer-events-auto text-base-white"
                    >
                        SHARE
                    </p>
                    <div
                        class="pointer-events-auto flex flex-col gap-[2.03vw] xs:gap-[8px]"
                    >
                        <div
                            class="slt-share-icon"
                            @click="handleShareToLine"
                            v-sendGAsignal="{
                                event_category: `/gplay/${id}`,
                                event_label: '分享至 line',
                            }"
                        >
                            <SvgLine24 class="icon" />
                        </div>
                        <div
                            class="slt-share-icon"
                            @click="handleShareToFacebook"
                            v-sendGAsignal="{
                                event_category: `/gplay/${id}`,
                                event_label: '分享至 facebook',
                            }"
                        >
                            <SvgFb24 class="icon" />
                        </div>
                        <div
                            class="slt-share-icon"
                            @click="handleShareToTwitter"
                            v-sendGAsignal="{
                                event_category: `/gplay/${id}`,
                                event_label: '分享至 twitter',
                            }"
                        >
                            <SvgTwitter24 class="icon" />
                        </div>
                        <div
                            v-if="canCopy"
                            id="copy-button-d"
                            :data-clipboard-text="shareURL"
                            class="slt-share-icon"
                            v-sendGAsignal="{
                                event_category: `/gplay/${id}`,
                                event_label: '複製網址',
                            }"
                        >
                            <SvgLink24 class="icon" />
                        </div>
                        <div
                            v-else-if="canUseClipboard"
                            id="copy-button-d"
                            @click="copyURL"
                            class="slt-share-icon"
                            v-sendGAsignal="{
                                event_category: `/gplay/${id}`,
                                event_label: '複製網址',
                            }"
                        >
                            <SvgLink24 class="icon" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
._button-close {
    @apply absolute right-[4.07vw] top-[4.07vw] z-[5] h-[10.18vw] rounded-full bg-primary-orange px-[4.07vw] xs:right-[16px] xs:top-[16px] xs:h-[40px] xs:px-[16px] sm:right-[32px] sm:top-[32px] sm:h-[48px] sm:px-[20px];
}

.slt-share-icon {
    @apply flex size-[10.17vw] cursor-pointer items-center justify-center rounded-full bg-base-Metal xs:h-[40px] xs:w-[40px];
}

.slt-share-icon svg {
    @apply size-[6.10vw] xs:size-[24px];
}

.slt-vertical-text {
    writing-mode: vertical-rl;
    text-orientation: mixed;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s linear;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}
</style>
